import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { courseService } from "@/services/course.service";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import type { Theme } from "@/types/api";

const formSchema = z.object({
  title: z.string().min(2, "Название должно содержать не менее 2 символов"),
  slug: z.string().optional().refine(
    (val) => !val || (val.length >= 2 && val.length <= 50 && /^[a-z0-9-]+$/.test(val)),
    "Слаг может содержать только английские буквы (a-z), цифры и дефисы (макс. 50 символов)"
  ),
});
type FormValues = z.infer<typeof formSchema>;

interface ThemeDialogProps {
  theme: Theme | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function ThemeDialog({
  theme,
  open,
  onOpenChange,
  onSuccess,
}: ThemeDialogProps) {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      slug: "",
    },
  });

  useEffect(() => {
    if (theme) {
      form.reset({
        title: theme.title,
        slug: undefined,
      });
    } else {
      form.reset({
        title: "",
        slug: "",
      });
    }
  }, [theme, form, open]);

  const onSubmit = async (values: FormValues) => {
    setIsLoading(true);
    try {
      if (theme) {
        // Use values as FormValues to satisfy the required title property
        await courseService.updateTheme(theme.slug, { title: values.title });
        toast({
          title: "Успешно",
          description: "Тема успешно обновлена",
        });
      } else {
        await courseService.createTheme({ title: values.title, slug: values.slug || "" });
        toast({
          title: "Успешно",
          description: "Тема успешно создана",
        });
      }
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error(error);
      toast({
        title: "Ошибка",
        description: `Не удалось ${theme ? "обновить" : "создать"} тему`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{theme ? "Редактировать тему" : "Создать тему"}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Название</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {!theme && (
              <FormField
                control={form.control}
                name="slug"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Слаг</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="artificial-intelligence" maxLength={50} />
                    </FormControl>
                    <FormDescription>
                      Используйте только английские буквы, цифры и дефисы (макс. 50 символов)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <DialogFooter>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {theme ? "Сохранение..." : "Создание..."}
                  </>
                ) : (
                  theme ? "Сохранить изменения" : "Создать"
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
